import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getMapperGaps, linkPayer, linkPayee, linkPayeeNpi } from "@/lib/eobEraV2";
import { describeError } from "@/lib/api";
import { readiness, estCharge, patientOf, payerOf } from "@/lib/claimReadiness";
import { RollingNumber } from "@/components/Skeletons";

const money = (v) => (v > 0 ? `$${Math.round(v).toLocaleString()}` : "$0");
const sm = (f) => { const s = f?.summary || f?.extraction_summary; if (!s) return {}; if (typeof s === "string") { try { return JSON.parse(s); } catch { return {}; } } return s; };
const amt = (f) => { const s = sm(f); const v = s.check_amount ?? s.total_paid; return isNaN(Number(v)) ? 0 : Number(v); };
const pyr = (f) => sm(f).payer_name || f.payer_name || "Unknown payer";
const pye = (f) => sm(f).payee_name || f.payee_name || "Unknown payee";
const cdate = (f) => sm(f).check_date || null;
const days = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);
const ageTxt = (d) => (d == null ? "—" : d > 365 ? `${Math.floor(d / 365)}y ${d % 365}d` : `${d}d`);
const norm = (s) => String(s || "").trim().toLowerCase();
const isDupCopy = (f) => { const d = f.duplicate; return !!(d && d.is_duplicate && !d.is_original); };
const auditC = (f) => { const ev = (f.era_validation || {}).reconciliation?.summary || {}; const of = ev.claims_census ?? ev.claims_extracted ?? (sm(f).claim_count || 0); return { ag: ev.claims_verified ?? of, of, flagged: ev.claims_flagged || 0 }; };
const auditBad = (f) => { const { ag, of, flagged } = auditC(f); return (of && ag < of) || flagged > 0 || (f.audit_status && f.audit_status !== "complete"); };
const unbal = (f) => { const s = sm(f); return s.reconciliation_flagged === true || s.reconciliation_verified === false || (s.reconciliation_status && !["verified", "healed", "balanced"].includes(s.reconciliation_status)); };
const isFailed = (f) => ["failed", "error"].includes((f.status || "").toLowerCase());
const isProc = (f) => ["processing", "queued", "in_progress", "pending"].includes((f.status || "").toLowerCase());

const Tag = ({ kind, children }) => {
  const c = kind === "held" ? ["#FDEDEC", "#C4302A"] : kind === "risk" ? ["#FBEEDD", "#9A5A00"] : ["#EEF0FF", "#4B4BBF"];
  return <span className="text-[9.5px] font-bold uppercase tracking-[.04em] rounded px-1.5 py-[1px]" style={{ background: c[0], color: c[1] }}>{children}</span>;
};

export default function NeedsYouBrief({ eobFiles, enriched, navigate, onOpenClaim, eobsReload, onCount }) {
  const [gaps, setGaps] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [seg, setSeg] = useState("all"); // all | rem | claim — organises the list, not the header
  const [ladder, setLadder] = useState(null); // {key,val,busy}
  useEffect(() => { getMapperGaps().then((r) => setGaps(r.gaps || [])).catch(() => {}); }, [eobFiles]);

  const files = eobFiles || [];
  const rows = enriched || [];

  const gapMatch = (f) => gaps.find((g) => norm(pyr(f)) === norm(g.payer) && (!g.payee || norm(pye(f)) === norm(g.payee)));
  const isHeld = (f) => !isDupCopy(f) && !isFailed(f) && !isProc(f) && f.era_status !== "era_generated" && (unbal(f) || auditBad(f) || !!gapMatch(f));

  // ---- header: two directions of money ----
  const held = useMemo(() => files.filter(isHeld), [files, gaps]);
  const eras = useMemo(() => files.filter((f) => !isDupCopy(f) && f.era_status === "era_generated"), [files]);
  const heldSum = held.reduce((s, f) => s + amt(f), 0);
  const eraSum = eras.reduce((s, f) => s + amt(f), 0);
  const oldestHeld = Math.max(0, ...held.map((f) => days(cdate(f)) || 0));

  // ---- claims (honest readiness model) ----
  const blockedRows = useMemo(() => rows.filter(({ r }) => r.blockers.length > 0), [rows]);
  const cleanRows = useMemo(() => rows.filter(({ r }) => r.bucket === "ready"), [rows]);
  const billBlockedSum = blockedRows.reduce((s, { n }) => s + estCharge(n), 0);
  const cleanSum = cleanRows.reduce((s, { n }) => s + estCharge(n), 0);
  const insRows = useMemo(() => blockedRows.filter(({ r }) => r.blockers.includes("no_payer") && !r.blockers.includes("no_dos") && !r.blockers.includes("no_cpt")), [blockedRows]);
  const dxRows = useMemo(() => rows.filter(({ r }) => r.blockers.includes("unlinked")), [rows]);

  // ---- two subjects, two states each (header groups) ----
  const copies = useMemo(() => files.filter(isDupCopy), [files]);
  const rem = {
    held: { sum: heldSum, count: held.length, oldest: oldestHeld },
    ready: { sum: eraSum, count: eras.length },
    dupCount: copies.length,
  };
  const clm = {
    held: { sum: billBlockedSum, blocked: blockedRows.length, total: rows.length },
    ready: { sum: cleanSum, count: cleanRows.length },
  };

  // ---- findings, ranked by money released per action ----
  const findings = useMemo(() => {
    const out = [];
    // account-level mapping links — one field clears every future remittance (highest leverage)
    const byGap = {};
    held.forEach((f) => { const g = gapMatch(f); if (!g) return; const k = `${(g.missing || [])[0]}::${g.payer}`; (byGap[k] ||= { g, files: [] }).files.push(f); });
    Object.entries(byGap).forEach(([key, v]) => {
      const sum = v.files.reduce((s, f) => s + amt(f), 0);
      const field = (v.g.missing || [])[0];
      out.push({ id: key, rank: sum, kind: "held", pipeline: "rem", write: field, entity: field === "edi_payer_id" ? v.g.payer : (v.g.payee || pye(v.files[0])),
        t: <>One field unblocks <span className="text-[#C4302A]">{money(sum)}</span> — and every {v.g.payer} remittance after it.</>,
        why: `${v.files.length} held remittance${v.files.length === 1 ? "" : "s"} wait on the same missing ${field}. It's an account mapping, not a property of these files, so linking once clears them and everything ${v.g.payer} sends next.`,
        ev: [["held", "cash held"], null, `${v.files.length} held`, `oldest ${ageTxt(Math.max(0, ...v.files.map((f) => days(cdate(f)) || 0)))}`, field.toUpperCase()],
        val: money(sum), vk: "Releases now", cert: "in the bank" });
    });
    // aged held cash — sitting over 90 days on the check date
    const aged = held.filter((f) => (days(cdate(f)) || 0) > 90);
    if (aged.length) {
      const sum = aged.reduce((s, f) => s + amt(f), 0);
      out.push({ id: "aged", rank: sum * 0.5, pipeline: "rem", to: "/payments-v2", go: "Open the ERA Builder →", pass: true,
        t: <><span className="text-[#C4302A]">{money(sum)}</span> has been sitting over 90 days.</>,
        why: `${aged.length} remittance${aged.length === 1 ? "" : "s"} held on gates, not on anything a payer is doing — oldest ${ageTxt(Math.max(0, ...aged.map((f) => days(cdate(f)) || 0)))}, aged on the check date.`,
        ev: [["held", "cash held"], `${aged.length} remittance${aged.length === 1 ? "" : "s"}`, "aged on check date"], val: money(sum), vk: "Held", cert: "in the bank" });
    }
    // claims one insurance link from billable
    if (insRows.length) {
      const sum = insRows.reduce((s, { n }) => s + estCharge(n), 0);
      out.push({ id: "ins", rank: sum * 0.4, pipeline: "claim", to: "/create-claims", go: "Work them →", pass: true, kind: "exp",
        t: <>{insRows.length} claim{insRows.length === 1 ? " is" : "s are"} one insurance link from billable — <span className="text-[#9A5A00]">{money(sum)}</span> expected.</>,
        why: "Insurance is the blocker on these, and revQ can patch or scan it. Claims also missing a date of service aren't counted here — there's no endpoint to write a DOS.",
        ev: [["exp", "expected"], `${insRows.length} of ${blockedRows.length} blocked`, "insurance blocker"], val: money(sum), vk: "Expected", cert: "a corpus estimate" });
    }
    // a service line with no diagnosis pointer will reject
    if (dxRows.length) {
      const n0 = dxRows[0].n; const sum = dxRows.reduce((s, { n }) => s + estCharge(n), 0);
      out.push({ id: "dx", rank: estCharge(n0), pipeline: "claim", openClaim: n0, go: "Open the claim →", pass: true, kind: "exp",
        t: dxRows.length === 1 ? <>A claim line on {patientOf(n0)} has no diagnosis pointer. It will reject before adjudication.</> : <>{dxRows.length} claims have a service line with no diagnosis pointer — they reject before adjudication.</>,
        why: "A CPT line that references no Box 21 diagnosis is rejected at the clearinghouse, so it never reaches the payer to be paid or denied. Auto-linking the primary diagnosis clears it.",
        ev: [["exp", "expected"], ["risk", "at risk"], patientOf(n0), payerOf(n0) || "no payer", `${dxRows.length} unpointed`], val: money(sum), vk: "At risk", cert: "a corpus estimate" });
    }
    return out.sort((a, b) => b.rank - a.rank);
  }, [files, gaps, held, insRows, dxRows]);

  const segCounts = { all: findings.length, rem: findings.filter((f) => f.pipeline === "rem").length, claim: findings.filter((f) => f.pipeline === "claim").length };
  const segList = seg === "all" ? findings : findings.filter((f) => f.pipeline === seg);
  const shown = showAll ? segList : segList.slice(0, 3);
  useEffect(() => { onCount?.(findings.length); }, [findings.length, onCount]);
  useEffect(() => { setShowAll(false); }, [seg]);

  const doLink = async (fd) => {
    const val = (ladder?.val || "").trim(); if (!val) return;
    setLadder((l) => ({ ...l, busy: true }));
    try {
      if (fd.write === "edi_payer_id") await linkPayer(fd.entity, val);
      else if (fd.write === "payee_npi") await linkPayeeNpi(fd.entity, val);
      else await linkPayee(fd.entity, val);
      toast.success(`Linked ${fd.entity}`); setLadder(null); await eobsReload?.();
      getMapperGaps().then((r) => setGaps(r.gaps || [])).catch(() => {});
    } catch (e) { toast.error(describeError(e)); setLadder((l) => ({ ...l, busy: false })); }
  };

  const linkLabel = (w) => (w === "edi_payer_id" ? "payer ID" : w === "payee_npi" ? "NPI" : "client ID");
  const act = (fd) => { if (fd.openClaim) onOpenClaim?.([fd.openClaim], 0); else navigate(fd.to); };

  // Build rule (kept in code, never shown): findings rank by money released per action, age as tiebreak;
  // duplicates are a standing file-store condition, not a finding, so they live in the Remittances heading
  // as a persistent count chip — no money, because deleting a copy prevents a double post, it releases nothing.
  const Pair = ({ label, value, sub, dot, onClick, testid }) => (
    <div className={`min-w-0 flex-1 ${onClick ? "cursor-pointer group" : ""}`} onClick={onClick} data-testid={testid}
      role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}>
      <div className="text-[10px] font-bold uppercase tracking-[.06em] text-[var(--gray2)] flex items-center gap-1.5">
        {dot ? <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: dot }} /> : null}{label}
      </div>
      <div className={`text-[19px] font-bold text-[var(--ink)] tabular-nums leading-none mt-2 ${onClick ? "group-hover:underline" : ""}`}><RollingNumber value={value} format={money} /></div>
      <div className="text-[11px] text-[#9B9B97] mt-1.5 leading-snug">{sub}</div>
    </div>
  );
  const GroupTitle = ({ children, chip }) => (
    <div className="flex items-center gap-2.5 mb-3">
      <span className="text-[11px] font-bold uppercase tracking-[.07em] text-[var(--ink)]">{children}</span>
      {chip || null}
    </div>
  );

  return (
    <div className="glass-surface rounded-[20px]" data-testid="home-needsyou-brief">
      {/* compact header — title, then two divider-separated groups, borderless on one baseline */}
      <div className="flex flex-col lg:flex-row" data-testid="brief-groups">
        <div className="lg:w-[236px] flex-shrink-0 p-5 lg:pr-6 lg:border-r border-black/[.07]">
          <h2 className="text-[14px] font-semibold text-[var(--ink)] leading-tight tracking-[-.01em]">Needs you today</h2>
          <div className="text-[12px] text-[var(--gray2)] mt-1.5" data-testid="brief-count">{findings.length} finding{findings.length === 1 ? "" : "s"} · most worth doing first</div>
        </div>

        <div className="flex-1 p-5 lg:px-6 lg:border-r border-black/[.07]" data-testid="brief-group-rem">
          <GroupTitle chip={rem.dupCount ? (
            <button data-testid="brief-dup-chip" onClick={() => navigate("/payments-v2?copies=1")}
              className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold rounded-[20px] px-2.5 py-[3px] transition hover:brightness-[.97]"
              style={{ background: "#FDF4E5", border: "1px solid #F0E0C4", color: "#9A5A00" }}>
              <span className="w-[5px] h-[5px] rounded-full" style={{ background: "#FF9500" }} />
              {rem.dupCount} duplicate cop{rem.dupCount === 1 ? "y" : "ies"}
            </button>
          ) : null}>Remittances</GroupTitle>
          <div className="flex gap-6">
            <Pair label="Held" dot="#E5484D" value={money(rem.held.sum)} sub={`${rem.held.count} remittance${rem.held.count === 1 ? "" : "s"} · oldest ${ageTxt(rem.held.oldest)}`} onClick={() => navigate("/payments-v2?seg=held")} testid="brief-rem-held" />
            <Pair label="Ready to generate" value={money(rem.ready.sum)} sub={`${rem.ready.count} remittance${rem.ready.count === 1 ? "" : "s"} · cash in hand`} />
          </div>
          <button onClick={() => navigate("/payments-v2")} data-testid="brief-rem-link" className="text-[12px] font-semibold text-[var(--ink)] hover:underline mt-3">ERA Builder →</button>
        </div>

        <div className="flex-1 p-5 lg:px-6" data-testid="brief-group-claim">
          <GroupTitle>Claims</GroupTitle>
          <div className="flex gap-6">
            <Pair label="Held" dot="#FF9500" value={money(clm.held.sum)} sub={`${clm.held.blocked} of ${clm.held.total} · expected, not cash`} />
            <Pair label="Ready to bill" value={money(clm.ready.sum)} sub={`${clm.ready.count} claim${clm.ready.count === 1 ? "" : "s"} clean · expected`} />
          </div>
          <button onClick={() => navigate("/create-claims")} data-testid="brief-claim-link" className="text-[12px] font-semibold text-[var(--ink)] hover:underline mt-3">Create Claims →</button>
        </div>
      </div>

      {/* segment — organises the list only; header above always shows both pipelines */}
      <div className="flex items-center gap-2 px-5 py-3 border-t border-black/[.07]" data-testid="brief-segment">
        {[["all", "Everything"], ["rem", "Remittances"], ["claim", "Claims"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setSeg(k)} data-testid={`brief-seg-${k}`}
            className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold rounded-full px-3 py-1.5 border transition ${seg === k ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white text-[var(--gray2)] border-black/[.12] hover:border-black/[.28]"}`}>
            {lbl} <b>{segCounts[k]}</b>
          </button>
        ))}
      </div>

      <div className="px-5 pb-2" data-testid="brief-findings" style={{ minHeight: 236 }}>
        {shown.length === 0 ? <div className="text-[12px] text-[var(--gray2)] py-4">{seg === "rem" ? "No remittance findings — nothing held on this side." : seg === "claim" ? "No claim findings — nothing held on this side." : "Nothing needs you right now — both directions are clear."}</div> : null}
        {shown.map((fd, i) => (
          <div key={fd.id} data-testid={`brief-find-${i}`}>
            <div className="flex items-start gap-3 py-3 flex-wrap" style={{ borderBottom: "1px solid #F1F1F4" }}>
              <div className="text-[13px] font-extrabold text-[#C7C7CC] tabular-nums w-[16px] flex-shrink-0 mt-0.5">{i + 1}</div>
              <div className="flex-1 min-w-[220px]">
                <div className="text-[13px] font-bold text-[var(--ink)] leading-snug">{fd.t}</div>
                <div className="text-[11.5px] text-[var(--gray2)] mt-1 leading-snug">{fd.why}</div>
                <div className="flex items-center gap-1.5 flex-wrap mt-1.5 text-[10.5px] text-[#9B9B97]">
                  {fd.ev.map((e, j) => e == null ? <span key={j} className="text-[#C7C7CC]">·</span> : Array.isArray(e) ? <Tag key={j} kind={e[0]}>{e[1]}</Tag> : <span key={j}>{e}</span>)}
                </div>
              </div>
              <div className="text-right w-[92px] flex-shrink-0">
                <div className="text-[15px] font-extrabold text-[var(--ink)] tabular-nums"><RollingNumber value={fd.val} format={money} /></div>
                <div className="text-[9.5px] text-[#9B9B97] uppercase tracking-wide font-semibold">{fd.vk}</div>
                <div className="text-[9.5px] text-[#B8B8BD]">{fd.cert}</div>
              </div>
              <div className="w-[152px] flex-shrink-0 text-right">
                {fd.write ? (
                  <button onClick={() => setLadder(ladder?.key === fd.id ? null : { key: fd.id, val: "" })} data-testid={`brief-link-${i}`} className="text-[11.5px] font-bold text-white bg-[var(--ink)] rounded-[9px] px-3 py-1.5">Link {linkLabel(fd.write)}</button>
                ) : (
                  <button onClick={() => act(fd)} data-testid={`brief-act-${i}`} className="text-[11.5px] font-semibold rounded-[9px] px-3 py-1.5 border border-black/[.15] hover:bg-black/[.04]">{fd.go}</button>
                )}
                <div className="text-[9.5px] text-[#9B9B97] mt-1">{fd.write ? (
                  <button data-testid={`brief-deep-${i}`} onClick={() => navigate(`/payments-v2?seg=held&sub=miss:${fd.write === "edi_payer_id" ? "payer_id" : fd.write}`)} className="text-[#5856D6] font-semibold hover:underline">see the held checks →</button>
                ) : fd.pass ? "needs a pass, not one action" : "opens the ERA Builder"}</div>
              </div>
            </div>
            {ladder?.key === fd.id ? (
              <div className="bg-black/[.03] rounded-[10px] p-3 flex items-end gap-2 flex-wrap my-1" data-testid={`brief-ladder-${i}`}>
                <div className="flex-1 min-w-[200px]">
                  <label className="text-[10px] font-semibold text-[var(--gray3)] uppercase">{fd.write.replace(/_/g, " ")}</label>
                  <input value={ladder.val} onChange={(e) => setLadder((l) => ({ ...l, val: e.target.value }))} placeholder="1–12 letters or digits" data-testid={`brief-linkinput-${i}`} className="w-full mt-1 px-3 py-2 rounded-[9px] border border-black/[.12] text-[12.5px] bg-white" />
                </div>
                <span className="flex-1 text-[10.5px] text-[var(--gray3)] min-w-[160px]">applies to {fd.entity} across the account</span>
                <button onClick={() => setLadder(null)} className="text-[11.5px] font-semibold text-[var(--gray2)] px-3 py-2">Cancel</button>
                <button onClick={() => doLink(fd)} disabled={ladder.busy || !ladder.val.trim()} data-testid={`brief-linkcommit-${i}`} className="text-[12px] font-bold text-white bg-[var(--ink)] rounded-[9px] px-4 py-2 disabled:opacity-40">{ladder.busy ? "Linking…" : "Link it"}</button>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {/* Build rule (code-only, never shown): one filled button = the single account-level write on this page;
          outlined buttons only open a page. The layout enforces it, so no footer sentence explains it. */}
      {segList.length > 3 ? (
        <div className="flex px-5 pb-5 pt-1">
          <button onClick={() => setShowAll((v) => !v)} data-testid="brief-more" className="text-[11.5px] font-semibold text-[#5856D6] hover:underline">{showAll ? "Show less ↑" : `Show ${segList.length - 3} more ↓`}</button>
        </div>
      ) : null}
    </div>
  );
}
